import React, { useCallback, useMemo } from 'react';
import { CButton, CDataTable, CModalBody, CModal, CModalHeader, CModalTitle } from '@coreui/react';
import _t from 'counterpart';
import { useMutation, useQuery } from 'react-query';
import { assignSalesStatusToCustomer, getSalesStatuses, updateCustomer } from '../../../services/BackendService';
import { ISalesStatus } from '../../salesStatus/types';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../../../helpers';
import Error from '../../../components/Error';

interface IProps {
  show: boolean;
  onClose: (refetch?: boolean) => void;
  customerId: string;
}

interface IMutationsParams {
  customerId: string;
  salesStatusId: number;
}

const AssignSalesStatusModal = ({ show, onClose, customerId }: IProps) => {
  const salesStatusesQuery = useQuery('sales-statuses', () => getSalesStatuses());
  const assignSalesStatusMutation = useMutation(
    ['assign-sales-status'],
    ({ customerId, salesStatusId }: IMutationsParams) => assignSalesStatusToCustomer(customerId, salesStatusId),
    {
      onSuccess: () => {
        toast.success(_t('sales-status.sales-status-assigned'));
        onClose(true);
      },
      onError: (error: any) => {
        toast.error(extractErrorMessage(error));
      },
    }
  );

  const fields = useMemo(
    () => [
      { key: 'status', label: _t('global.status'), sorter: true, _style: { width: '90%' } },
      { key: 'actions', label: _t('global.actions'), sorter: false },
    ],
    []
  );

  const invokeMutation = useCallback(
    (salesStatusId: number) => {
      assignSalesStatusMutation.mutate({
        customerId,
        salesStatusId,
      });
    },
    [assignSalesStatusMutation, customerId]
  );

  const slots = useMemo(
    () => ({
      actions: (status: ISalesStatus) => (
        <td>
          <CButton color="primary" size="sm" onClick={() => invokeMutation(status.id)}>
            {_t('action.set')}
          </CButton>
        </td>
      ),
    }),
    [invokeMutation]
  );

  return (
    <CModal show={show} onClose={onClose} size="xl">
      <CModalHeader>
        <CModalTitle>{_t('sales-status.assign-sales-status')}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {salesStatusesQuery.isError && <Error onRetry={salesStatusesQuery.refetch} />}
        {!salesStatusesQuery.isError && (
          <CDataTable
            fields={fields}
            scopedSlots={slots}
            items={salesStatusesQuery.data || []}
            loading={salesStatusesQuery.isLoading}
          />
        )}
      </CModalBody>
    </CModal>
  );
};

export default AssignSalesStatusModal;
